import { lerDecimal } from '@equiview/domain';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { SemCadastro } from '../components/SemCadastro.js';
import { Aguardar, BarraAcoes, Botao, BotaoLink, CabecalhoPagina, Campo, CampoTexto, classeCampo, EstadoVazio, OpcoesRadio, Painel } from '../components/ui/index.js';
import { useConsulta, useFonte, usePodeCadastrar } from '../dados/contexto.js';
import type { Visita } from '../dados/fonte.js';

/** Dados da visita: data, numero e condicoes do dia. So enquanto ela nao foi encerrada. */
export function EditarVisita() {
  const { id = '' } = useParams();
  const fonte = useFonte();
  const equipe = usePodeCadastrar();
  const consulta = useConsulta((f) => (f.cadastro ? f.cadastro.visita(id) : Promise.resolve(null)), [id]);

  if (!fonte.cadastro || !equipe) return <SemCadastro demo={fonte.ficticia} />;

  return (
    <Aguardar consulta={consulta}>
      {(v) =>
        !v ? (
          <EstadoVazio titulo="Visita não encontrada" acao={<BotaoLink to="/visitas">Voltar às visitas</BotaoLink>} />
        ) : v.closed_at ? (
          <EstadoVazio titulo="Visita encerrada" acao={<BotaoLink to={`/visitas/${v.id}`}>Voltar à visita</BotaoLink>}>
            Visita encerrada não muda mais.
          </EstadoVazio>
        ) : (
          <Form visita={v} />
        )
      }
    </Aguardar>
  );
}

function Form({ visita }: { visita: Visita }) {
  const cadastro = useFonte().cadastro!;
  const navegar = useNavigate();
  const [data, setData] = useState(visita.visit_date);
  const [numero, setNumero] = useState(String(visita.visit_number));
  const [clima, setClima] = useState<NonNullable<Visita['weather']> | ''>(visita.weather ?? '');
  const [temperatura, setTemperatura] = useState(visita.temp_c == null ? '' : String(visita.temp_c).replace('.', ','));
  const [notas, setNotas] = useState(visita.notes ?? '');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const salvar = async () => {
    setErro(null);
    const n = Number(numero);
    const temp = lerDecimal(temperatura, 1);
    if (!data || !Number.isInteger(n) || n < 1) return setErro('Informe a data e um número de visita válido.');
    if (temp != null && Number.isNaN(temp)) return setErro('Temperatura inválida.');
    setSalvando(true);
    try {
      await cadastro.salvarVisita(visita.id, {
        visit_date: data,
        visit_number: n,
        weather: clima || null,
        temp_c: temp,
        notes: notas.trim() || null,
      });
      navegar(`/visitas/${visita.id}`, { replace: true });
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="space-y-6">
      <CabecalhoPagina
        trilha={[
          { rotulo: 'Visitas', para: '/visitas' },
          { rotulo: `Visita nº ${visita.visit_number}`, para: `/visitas/${visita.id}` },
          { rotulo: 'Editar' },
        ]}
        titulo="Editar visita"
      />
      <Painel titulo="Visita">
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-[12rem_8rem]">
            <CampoTexto rotulo="Data" obrigatorio type="date" value={data} onChange={(e) => setData(e.target.value)} />
            <CampoTexto
              rotulo="Número"
              obrigatorio
              inputMode="numeric"
              value={numero}
              onChange={(e) => setNumero(e.target.value.replace(/\D/g, ''))}
            />
          </div>
          <OpcoesRadio
            rotulo="Clima"
            valor={clima}
            onMudar={setClima}
            opcoes={[
              { valor: 'sol', rotulo: 'Sol' },
              { valor: 'nublado', rotulo: 'Nublado' },
              { valor: 'chuva', rotulo: 'Chuva' },
            ]}
          />
          <CampoTexto
            rotulo="Temperatura (°C)"
            inputMode="decimal"
            value={temperatura}
            onChange={(e) => setTemperatura(e.target.value)}
            className="max-w-40"
          />
          <Campo rotulo="Observações">
            {(a) => <textarea {...a} rows={3} value={notas} onChange={(e) => setNotas(e.target.value)} className={classeCampo()} />}
          </Campo>
          {erro && <p className="text-sm text-erro">{erro}</p>}
        </div>
      </Painel>
      <BarraAcoes>
        <BotaoLink variante="discreto" to={`/visitas/${visita.id}`}>
          Cancelar
        </BotaoLink>
        <Botao variante="principal" carregando={salvando} onClick={() => void salvar()}>
          Salvar
        </Botao>
      </BarraAcoes>
    </div>
  );
}
